import { apiId } from '../utils/apiId';
import { UnifiedBundle, UnifiedBundleItem, Product, ProductType } from '../types/Product';
import { isCurrencySet } from '../utils/currency';
import { multiplyDecimalByInt, sumDecimal } from '../utils/decimal';
import { supplierCostFor } from '../utils/supplierPrice';
import { apiGet, apiPost, apiPut, apiDelete, planningPath } from './PlanningApiClient';

export const BUNDLE_CHANGE_EVENT = 'anojeje_bundle_change';

// ── In-memory cache (source of truth after first API fetch) ──────────────────
let bundlesCache: UnifiedBundle[] | null = null;

// ── Migration: promote old favour bundles from localStorage (one-time) ────────

const OLD_FAVOUR_KEY = 'anojeje_favour_bundles';
const MIGRATION_KEY = 'anojeje_bundles_migrated_v1';

/**
 * One-time promotion of legacy favour bundles into the API.
 *
 * Genuinely best-effort — it runs off a render path with no user action to
 * report against, so it stays fire-and-forget and stays synchronous. What it
 * must not do is destroy its own source: it used to delete OLD_FAVOUR_KEY and
 * set the migrated flag immediately, so a POST that failed took the only copy
 * of those bundles with it and the migration never ran again. The source is
 * now discarded only once every write has landed, leaving a failed run to
 * retry harmlessly on the next load.
 */
function migrateFavourBundlesOnce(): void {
  if (localStorage.getItem(MIGRATION_KEY)) return;

  let old: any[];
  try {
    const raw = localStorage.getItem(OLD_FAVOUR_KEY);
    if (!raw) { localStorage.setItem(MIGRATION_KEY, '1'); return; }
    old = JSON.parse(raw);
  } catch {
    // Unparseable legacy blob — nothing to promote, and retrying won't help.
    localStorage.setItem(MIGRATION_KEY, '1');
    return;
  }

  Promise.all(old.map(fb => apiPost(planningPath('/bundles'), {
    name: fb.name,
    description: fb.description ?? null,
    items: (fb.items ?? []).map((i: any) => ({
      productId: i.favourItemId ?? '',
      productName: i.favourItemName ?? '',
      quantity: i.quantity ?? 1,
      unitLabel: 'pcs',
    })),
    types: ['favour'],
    price_mode: 'manual',
    price: 0,
    recipient_ids: fb.recipientIds ?? [],
    is_active: true,
  }))).then(() => {
    localStorage.removeItem(OLD_FAVOUR_KEY);
    localStorage.setItem(MIGRATION_KEY, '1');
  }).catch(() => {
    // Keep the source and the unset flag so the next load tries again.
  });
}

function dispatchChange(): void {
  window.dispatchEvent(new Event(BUNDLE_CHANGE_EVENT));
}

// ── API mapping ───────────────────────────────────────────────────────────────

function mapApiRow(row: any): UnifiedBundle {
  return {
    id: String(row.id),
    name: row.name,
    description: row.description ?? undefined,
    image: row.image ?? undefined,
    items: (row.items ?? []).map((i: any): UnifiedBundleItem => ({
      productId: i.productId ?? i.fabricId ?? '',
      productName: i.productName ?? '',
      quantity: i.quantity ?? i.yards ?? 0,
      unitLabel: i.unitLabel ?? undefined,
    })),
    types: row.types ?? [],
    priceMode: row.price_mode ?? 'manual',
    price: Number(row.price ?? 0),
    eventIds: row.event_ids ?? [],
    is_active: row.is_active ?? true,
    recipientIds: row.recipient_ids ?? undefined,
    paymentReference: row.payment_reference ?? undefined,
    paymentInstructions: row.payment_instructions ?? undefined,
    targetCost: row.target_cost != null ? Number(row.target_cost) : undefined,
    targetCurrency: row.target_currency ?? undefined,
    archived: row.archived ?? false,
    deletedAt: row.deleted_at ?? null,
    created_at: row.created_at,
    updated_at: row.updated_at,
  };
}

function bundleToApiBody(data: Partial<Omit<UnifiedBundle, 'id' | 'created_at' | 'updated_at'>>): Record<string, unknown> {
  return {
    name: data.name,
    description: data.description ?? null,
    items: data.items ?? [],
    types: data.types ?? [],
    price_mode: data.priceMode ?? 'manual',
    price: data.price ?? 0,
    event_ids: data.eventIds ?? [],
    recipient_ids: data.recipientIds ?? [],
    is_active: data.is_active ?? true,
    payment_reference: data.paymentReference ?? null,
    payment_instructions: data.paymentInstructions ?? null,
    target_cost: data.targetCost ?? null,
    target_currency: data.targetCost != null ? (data.targetCurrency || null) : null,
  };
}

// ── Public API ────────────────────────────────────────────────────────────────

export function loadBundles(): UnifiedBundle[] {
  migrateFavourBundlesOnce();
  return bundlesCache ?? [];
}

export function filterBundlesByType(type: ProductType): UnifiedBundle[] {
  return loadBundles().filter(b => b.types.includes(type));
}

export async function refreshBundlesFromApi(): Promise<UnifiedBundle[]> {
  migrateFavourBundlesOnce();
  try {
    const res = await apiGet<{ bundles: any[] }>(planningPath('/bundles'));
    bundlesCache = (res.bundles ?? []).map(mapApiRow);
    dispatchChange();
    return bundlesCache;
  } catch {
    return bundlesCache ?? [];
  }
}

/**
 * Fetch archived bundles directly from the API — mirrors
 * ProductService.fetchArchivedProducts(). The backend's `?archived=true` is
 * strictly binary (only active or only archived, never both — see
 * ans-backend's listBundles), so an "all" view means combining this with the
 * regular cache client-side. Kept separate from bundlesCache/
 * refreshBundlesFromApi() on purpose: every existing caller of the shared
 * cache has always meant "active bundles only" and must keep meaning that.
 */
export async function fetchArchivedBundles(): Promise<UnifiedBundle[]> {
  const res = await apiGet<{ bundles: any[] }>(planningPath('/bundles?archived=true'));
  return (res.bundles ?? []).map(mapApiRow);
}

/** Bundles currently in trash (stage 2 of archive → trash → permanent-delete). */
export async function fetchTrashedBundles(): Promise<UnifiedBundle[]> {
  const res = await apiGet<{ bundles: any[] }>(planningPath('/bundles?trashed=true'));
  return (res.bundles ?? []).map(mapApiRow);
}

export async function createBundle(
  data: Omit<UnifiedBundle, 'id' | 'created_at' | 'updated_at'>
): Promise<UnifiedBundle> {
  // The server owns the id, and /bundles/:id parses it with Number() — the old
  // temp `bundle-<ts>` row was only swapped for the real one inside a .then()
  // with an empty .catch(), so a rejected POST left a bundle on screen that no
  // later PUT or DELETE could address.
  const created = await apiPost<any>(planningPath('/bundles'), bundleToApiBody(data));
  const bundle = mapApiRow(created);

  bundlesCache = [...(bundlesCache ?? []), bundle];
  dispatchChange();

  return bundle;
}

export async function updateBundle(
  id: string,
  updates: Partial<Omit<UnifiedBundle, 'id' | 'created_at'>>
): Promise<UnifiedBundle | null> {
  const current = bundlesCache ?? [];
  const idx = current.findIndex(b => b.id === id);
  if (idx === -1) return null;

  // The isNaN guard used to skip the write entirely and still report success,
  // which is how an un-persisted bundle's edits looked saved. It's a refusal
  // now, and the only ids that hit it are the temp ones createBundle no
  // longer mints.
  const serverId = apiId(id);
  if (!serverId) throw new Error('This bundle was never saved to the server — reload the page and try again.');

  // Persist before touching the cache, or a rejected PUT leaves the edit on
  // screen until the next refresh silently reverts it.
  await apiPut(planningPath(`/bundles/${serverId}`), bundleToApiBody(updates));

  const updated: UnifiedBundle = { ...current[idx], ...updates, updated_at: new Date().toISOString() };
  bundlesCache = current.map((b, i) => i === idx ? updated : b);
  dispatchChange();

  return updated;
}

/**
 * Permanent delete — stage 3 of archive → trash → permanent-delete. The
 * backend refuses (409) unless the bundle is already in trash, so this
 * doesn't check bundlesCache the way it used to: an archived or trashed
 * bundle was never in the active cache to begin with, and requiring it be
 * found there would make this un-callable for the case it exists to serve.
 */
export async function deleteBundle(id: string): Promise<void> {
  const serverId = apiId(id);
  if (!serverId) throw new Error('This bundle was never saved to the server — reload the page and try again.');

  await apiDelete(planningPath(`/bundles/${serverId}`));

  bundlesCache = (bundlesCache ?? []).filter(b => b.id !== id);
  dispatchChange();
}

/** Stage 1: hide from the active list, reversible via unarchiveBundle. */
export async function archiveBundle(id: string): Promise<UnifiedBundle> {
  const serverId = apiId(id);
  if (!serverId) throw new Error('This bundle was never saved to the server — reload the page and try again.');
  const updated = await apiPut<any>(planningPath(`/bundles/${serverId}`), { archived: true });
  bundlesCache = (bundlesCache ?? []).filter(b => b.id !== id);
  dispatchChange();
  return mapApiRow(updated);
}

/** Undoes archiveBundle — the bundle returns to the active list. */
export async function unarchiveBundle(id: string): Promise<UnifiedBundle> {
  const serverId = apiId(id);
  if (!serverId) throw new Error('This bundle was never saved to the server — reload the page and try again.');
  const updated = await apiPut<any>(planningPath(`/bundles/${serverId}`), { archived: false });
  const bundle = mapApiRow(updated);
  bundlesCache = [...(bundlesCache ?? []).filter(b => b.id !== id), bundle];
  dispatchChange();
  return bundle;
}

/**
 * Stage 2: move an already-archived bundle to trash. The backend refuses
 * (409, "Archive the bundle before moving it to trash.") if it isn't
 * archived yet — that message surfaces as-is via apiPost's error handling.
 */
export async function trashBundle(id: string): Promise<UnifiedBundle> {
  const serverId = apiId(id);
  if (!serverId) throw new Error('This bundle was never saved to the server — reload the page and try again.');
  const trashed = await apiPost<any>(planningPath(`/bundles/${serverId}/trash`), {});
  dispatchChange();
  return mapApiRow(trashed);
}

/** Pulls a bundle back out of trash — it lands in the archived list, not active. */
export async function restoreBundleFromTrash(id: string): Promise<UnifiedBundle> {
  const serverId = apiId(id);
  if (!serverId) throw new Error('This bundle was never saved to the server — reload the page and try again.');
  const restored = await apiPost<any>(planningPath(`/bundles/${serverId}/restore`), {});
  dispatchChange();
  return mapApiRow(restored);
}

/**
 * Sum of qty × preferred supplier cost, in the project `currency`, as an exact
 * decimal string. A component with no known cost (redacted, missing, no rate)
 * adds nothing; null when the project has no currency.
 */
export function calcAutoPrice(items: UnifiedBundleItem[], products: Product[], currency?: string | null): string | null {
  if (!isCurrencySet(currency)) return null;
  const parts = items.map(item => {
    const product = products.find(p => p.id === item.productId);
    if (!product) return null;
    const preferred = product.suppliers.find(s => s.isPreferred) ?? product.suppliers[0];
    return preferred ? multiplyDecimalByInt(supplierCostFor(preferred, currency), item.quantity) : null;
  });
  return sumDecimal(parts);
}
